import React from 'react';
import { Sparkles } from 'lucide-react';
import { isScanActive, useCancelHygieneScan, useHygieneEstimate } from '../../../hooks/hygiene/useHygiene.ts';
import type { HygieneCheckMeta, HygieneCoverage, HygieneScan } from '../../../request/hygiene/hygieneRequests.ts';
import { formatDate, formatDuration } from '../labels.ts';
import { DialogShell } from './HygieneDialogs.tsx';

const BATCH = 8;

function coverageLines(coverage: Record<string, HygieneCoverage> | undefined, checks: Map<string, HygieneCheckMeta>) {
  if (!coverage) return [];
  return Object.entries(coverage)
    .filter(([, c]) => c.eligible > 0)
    .map(([code, c]) => {
      const title = checks.get(code)?.title ?? code;
      const skipped = c.reviewed > 0 ? `, ${c.reviewed} already reviewed` : '';
      return `${title}: ${c.selected} of ${c.eligible}${skipped}`;
    });
}

/** Estimate → confirm dialog for starting an AI review. */
export function AIReviewStartDialog({
  auditYearId,
  checks,
  busy,
  onConfirm,
  onClose,
}: {
  auditYearId: string;
  checks: Map<string, HygieneCheckMeta>;
  busy: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useHygieneEstimate(auditYearId, true);
  const est = data?.estimate;
  const limitReached = Boolean(data && data.daily_limit > 0 && data.runs_today >= data.daily_limit);
  const lines = coverageLines(est?.coverage, checks);
  return (
    <DialogShell
      title="Run AI review"
      subtitle="Checks wording for duplicates, mismatched mappings and frequencies"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 shadow-sm hover:bg-gray-50" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            disabled={!est || est.items === 0 || limitReached || busy}
            onClick={onConfirm}
            className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Starting…' : 'Start review'}
          </button>
        </>
      }
    >
      {isLoading ? <p className="text-sm text-gray-500">Preparing the estimate…</p> : null}
      {isError ? <p className="text-sm text-red-700">The estimate could not be prepared.</p> : null}
      {est ? (
        est.items === 0 ? (
          <p className="text-sm text-gray-700">Nothing new to review: every item was already reviewed and has not changed since.</p>
        ) : (
          <>
            <dl className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <dt className="text-xs text-gray-500">Items</dt>
                <dd className="font-semibold text-gray-900">{est.items}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Time</dt>
                <dd className="font-semibold text-gray-900">{formatDuration(est.seconds)}</dd>
              </div>
              <div>
                <dt className="text-xs text-gray-500">Estimated cost</dt>
                <dd className="font-semibold text-gray-900">${est.cost_usd < 0.01 ? '<0.01' : est.cost_usd.toFixed(2)}</dd>
              </div>
            </dl>
            {lines.length ? (
              <ul className="space-y-0.5 text-sm text-gray-700">
                {lines.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            ) : null}
            <p className="text-xs text-gray-500">
              Results are suggestions: each one is confirmed or dismissed by a person. Up to {data?.item_cap} items per review{data && data.daily_limit > 0 ? `; ${data.runs_today} of ${data.daily_limit} reviews used today` : ''}.
            </p>
          </>
        )
      ) : null}
      {limitReached ? <p className="text-sm text-amber-800">This organization has used all of today’s AI reviews.</p> : null}
    </DialogShell>
  );
}

const STATUS_LABEL: Record<HygieneScan['status'], string> = {
  queued: 'starting',
  running: 'running',
  cancelling: 'stopping',
  completed: 'completed',
  failed: 'failed',
  cancelled: 'cancelled',
};

/**
 * Shows the AI review being followed (progress, cancel) or the last one's outcome. The page owns the polling
 * (so it knows when a run finished or stalled); this only renders what it is given.
 */
export function AIReviewStatus({
  scan,
  stalled,
  pollError,
  checks,
  canManage,
}: {
  scan: HygieneScan;
  stalled: boolean;
  pollError: boolean;
  checks: Map<string, HygieneCheckMeta>;
  canManage: boolean;
}) {
  const cancel = useCancelHygieneScan();
  const active = isScanActive(scan) && !stalled;
  const batches = Math.max(1, Math.ceil(scan.items_total / BATCH));
  const batchesDone = Math.min(batches, Math.ceil(scan.items_done / BATCH));
  const pct = scan.items_total ? Math.round((scan.items_done / scan.items_total) * 100) : 0;
  const lines = coverageLines(scan.coverage, checks);

  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/50 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="inline-flex items-center gap-2 font-medium text-gray-900">
          <Sparkles className="h-4 w-4 text-blue-600" aria-hidden />
          {active
            ? scan.status === 'cancelling'
              ? 'Stopping the AI review after the current batch…'
              : scan.status === 'queued'
                ? 'AI review starting…'
                : `AI review running · batch ${batchesDone} of ${batches}`
            : stalled
              ? 'AI review stopped responding'
              : `Last AI review ${formatDate(scan.finished_at ?? scan.started_at)} · ${STATUS_LABEL[scan.status]}`}
        </span>
        {active && canManage && scan.status !== 'cancelling' ? (
          <button
            type="button"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate(scan.id)}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
        ) : null}
      </div>
      {active ? (
        <div className="mt-2 h-2 overflow-hidden rounded-full bg-blue-100" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="AI review progress">
          <div className="h-full bg-blue-600 transition-all" style={{ width: `${pct}%` }} />
        </div>
      ) : null}
      {active && pollError ? <p className="mt-2 text-amber-800">Progress could not be refreshed; retrying…</p> : null}
      {stalled ? <p className="mt-2 text-amber-800">This review has stopped reporting progress. Start a new one to check the rest; finished items are kept.</p> : null}
      {!active && !stalled && scan.error_message ? <p className="mt-1 text-red-700">{scan.error_message}</p> : null}
      {lines.length ? <p className="mt-1 text-xs text-gray-600">Checked: {lines.join(' · ')}</p> : null}
    </div>
  );
}
